import { useSyncExternalStore } from 'react';

/**
 * KURULABİLİR PANEL (PWA) — tarayıcı tarafı durumu: servis çalışanı kaydı, bekleyen yeni
 * sürüm, kurulum istemi, bağlantı. React dışında tutulur: `beforeinstallprompt` olayı
 * uygulama çizilmeden önce gelebilir ve kaçarsa bir daha gelmez.
 */
type Istem = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> };

export type PwaDurum = {
  /** Yeni sürüm indirildi, devralmak için kullanıcının "Yenile"sini bekliyor. */
  yeniSurum: boolean;
  /** Android/Chrome/Edge: tarayıcının kurulum istemi elde. */
  istemVar: boolean;
  /** Ana ekrandan (standalone) açık. */
  kurulu: boolean;
  /** iPhone/iPad: istem yok, "Paylaş → Ana Ekrana Ekle" anlatılır. */
  ios: boolean;
  /** Tarayıcı çevrimdışı olduğunu bildiriyor. */
  cevrimdisi: boolean;
  /** Etkin servis çalışanının sürümü (yalnız üretim derlemesinde). */
  surum: string | null;
};

const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
const iosMu = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1);
const standaloneMu = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.matchMedia('(display-mode: fullscreen)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

let durum: PwaDurum = { yeniSurum: false, istemVar: false, kurulu: false, ios: iosMu, cevrimdisi: false, surum: null };
const dinleyenler = new Set<() => void>();
let istem: Istem | null = null;
let bekleyen: ServiceWorker | null = null;
let kayit: ServiceWorkerRegistration | null = null;

function yaz(d: Partial<PwaDurum>) {
  durum = { ...durum, ...d };
  dinleyenler.forEach((f) => f());
}

export function usePwa(): PwaDurum {
  return useSyncExternalStore((f) => { dinleyenler.add(f); return () => { dinleyenler.delete(f); }; }, () => durum, () => durum);
}

/** Tarayıcının kurulum penceresini açar. Sonuç: kabul / ret / istem yok. */
export async function kur(): Promise<'kabul' | 'ret' | 'yok'> {
  if (!istem) return 'yok';
  const i = istem;
  istem = null; // istem tek kullanımlıktır
  yaz({ istemVar: false });
  await i.prompt();
  const { outcome } = await i.userChoice;
  return outcome === 'accepted' ? 'kabul' : 'ret';
}

/**
 * "Yenile": bekleyen sürüm devralır, BU sekme bir kez yeniden yüklenir.
 * Öbür açık sekmeler kendiliğinden yenilenmez (açık formda veri kaybı olmasın): onlarda çubuk çıkar.
 */
let yenilemeIstendi = false;
export function yeniSurumeGec() {
  if (bekleyen) { yenilemeIstendi = true; bekleyen.postMessage({ tur: 'ATLA' }); window.setTimeout(() => window.location.reload(), 4000); }
  else window.location.reload();
}

/** Uzun süre açık kalan uygulamada yeni sürüm var mı diye sunucuya sorar (saatte en çok bir). */
let sonYoklama = 0;
export function surumYokla(zorla = false) {
  if (!kayit) return;
  const simdi = Date.now();
  if (!zorla && simdi - sonYoklama < 60 * 60 * 1000) return;
  sonYoklama = simdi;
  void kayit.update().catch(() => undefined);
}

type SurumYaniti = { surum: string; kabuk?: string[] };
function surumSor(sw: ServiceWorker | null): Promise<SurumYaniti | null> {
  return new Promise((coz) => {
    if (!sw) { coz(null); return; }
    try {
      const kanal = new MessageChannel();
      const z = window.setTimeout(() => coz(null), 2000);
      kanal.port1.onmessage = (e) => { window.clearTimeout(z); coz(e.data?.surum ? (e.data as SurumYaniti) : null); };
      sw.postMessage({ tur: 'SURUM' }, [kanal.port2]);
    } catch { coz(null); }
  });
}
/** Bu sayfanın yüklediği derleme dosyaları, verilen sürümün dosyaları mı? (Sayfa zaten o sürümün koduyla çalışıyor mu?) */
function sayfaBuSurumde(kabuk: string[] | undefined): boolean {
  if (!kabuk) return false;
  const benim = [...document.querySelectorAll<HTMLScriptElement>('script[src]')].map((x) => new URL(x.src).pathname)
    .concat([...document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href]')].map((x) => new URL(x.href).pathname))
    .filter((y) => y.startsWith('/assets/'));
  return benim.length > 0 && benim.every((y) => kabuk.includes(y));
}

/** Uygulama açılırken bir kez çağrılır (main.tsx). */
export function pwaBaslat(uretim: boolean) {
  yaz({ kurulu: standaloneMu(), cevrimdisi: navigator.onLine === false });

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // tarayıcının kendi küçük çubuğu yerine kendi kartımız
    istem = e as Istem;
    yaz({ istemVar: true });
  });
  window.addEventListener('appinstalled', () => { istem = null; yaz({ istemVar: false, kurulu: true }); });
  window.matchMedia('(display-mode: standalone)').addEventListener?.('change', () => yaz({ kurulu: standaloneMu() }));
  window.addEventListener('online', () => yaz({ cevrimdisi: false }));
  window.addEventListener('offline', () => yaz({ cevrimdisi: true }));

  // Geliştirmede kaydedilmez: bayat kabuk, değişikliklerin görünmemesine yol açar.
  if (!uretim || !('serviceWorker' in navigator)) return;
  const sw = navigator.serviceWorker;
  /** Kayıt anında sayfayı yöneten bir çalışan var mıydı? Yoksa bu İLK kurulumdur: çubuk gösterilmez, sayfa yenilenmez. */
  const oncedenVardi = Boolean(sw.controller);

  /**
   * Sayfayı yöneten çalışan değişti.
   *  · Bu sekmede "Yenile"ye basıldıysa: sayfa yeniden yüklenir.
   *  · Başka sekmede basıldıysa ya da çalışan kendiliğinden devraldıysa: YENİLENMEZ. Sayfa eski
   *    kodla çalışıyorsa çubuk gösterilir; zaten yeni kodla açılmışsa yapılacak bir şey yoktur.
   */
  sw.addEventListener('controllerchange', () => {
    if (yenilemeIstendi) { window.location.reload(); return; }
    void surumSor(sw.controller).then((y) => {
      if (y) yaz({ surum: y.surum });
      if (oncedenVardi && y && !sayfaBuSurumde(y.kabuk)) { bekleyen = null; yaz({ yeniSurum: true }); }
    });
  });

  const izle = (r: ServiceWorkerRegistration) => {
    const bekleyeniGor = () => {
      const w = r.waiting;
      if (!w || !sw.controller) return;
      void surumSor(w).then((y) => {
        // Sayfa dağıtımdan SONRA açıldıysa yeni kodu zaten ağdan almıştır: çubuk gerekmez, bekleyen çalışan
        // sessizce devralır (eski önbellek silinir). Hiçbir sekme yenilenmez.
        if (y && sayfaBuSurumde(y.kabuk)) { w.postMessage({ tur: 'ATLA' }); return; }
        bekleyen = w; yaz({ yeniSurum: true });
      });
    };
    bekleyeniGor();
    r.addEventListener('updatefound', () => {
      const yeni = r.installing;
      if (!yeni) return;
      yeni.addEventListener('statechange', () => { if (yeni.state === 'installed') bekleyeniGor(); });
    });
  };

  window.addEventListener('load', () => {
    sw.register('/sw.js', { scope: '/', updateViaCache: 'none' })
      .then((r) => { kayit = r; izle(r); void surumSor(sw.controller ?? r.active).then((y) => { if (y) yaz({ surum: y.surum }); }); })
      .catch(() => undefined);
  });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) surumYokla(); });
}

/* ─── Kurulum kartının susturulması ───────────────────────────────────────── */
const SUS = 'am_kurulum_sus';
const OTUZ_GUN = 30 * 24 * 60 * 60 * 1000;
export const kurulumSus = {
  /** "Daha sonra": 30 gün boyunca kart kendiliğinden çıkmaz (Ayarlar'daki satır hep durur). */
  yaz() { try { localStorage.setItem(SUS, String(Date.now())); } catch { /* özel pencere */ } },
  suskunMu(): boolean {
    try { const t = Number(localStorage.getItem(SUS)); return Number.isFinite(t) && t > 0 && Date.now() - t < OTUZ_GUN; } catch { return true; }
  },
};
